/**
 * Matrix Detail page - shows a generated permitting matrix
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiClient } from '../api/client';

interface PermitRequirement {
  category: string;
  requirement: string;
  agency: string;
  threshold: string;
  timeline: string;
  estimated_price: string;
  sources: string[];
}

interface MatrixSummary {
  total_cost_min: number | null;
  total_cost_max: number | null;
  total_cost_formatted: string;
  max_lead_time_days: number | null;
  max_lead_time_formatted: string;
  total_requirements: number;
}

interface MatrixResult {
  request_uuid: string;
  cached: boolean;
  processing_time_seconds: number;
  status?: string;
  result: {
    project_name?: string;
    state: string;
    county: string;
    city?: string;
    power_capacity_mw: number;
    project_type: string;
    cached?: boolean;
    requirements_flat?: PermitRequirement[];
    requirements?: PermitRequirement[];
    matrix_summary?: MatrixSummary;
    permitting_matrix?: {
      federal?: PermitRequirement[];
      state?: PermitRequirement[];
      county?: PermitRequirement[];
      local?: PermitRequirement[];
    };
    [key: string]: any;
  };
}

interface UserProfile {
  id: number;
  email: string;
  subscription_status: string;
  subscription_type: string | null;
  free_matrices_used: number;
  free_matrices_limit: number;
}

const LOADING_MESSAGES = [
  'Researching state regulations...',
  'Exploring county requirements...',
  'Analyzing federal permits...',
  'Reviewing local ordinances...',
  'Cross-referencing permit databases...',
  'Calculating timelines...',
  'Estimating costs...',
  'Compiling requirements...',
  'Validating thresholds...',
  'Building your matrix...',
];

export const MatrixDetail: React.FC = () => {
  const { uuid } = useParams<{ uuid: string }>();
  const [matrix, setMatrix] = useState<MatrixResult | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [loadingMessageIndex, setLoadingMessageIndex] = useState(0);
  const navigate = useNavigate();

  const loadMatrix = useCallback(async () => {
    try {
      const data = await apiClient.getMatrix(uuid!);
      console.log('Matrix data:', data);
      console.log('Matrix result:', data?.result);
      console.log('Project name:', data?.result?.project_name);
      console.log('Project type:', data?.result?.project_type);
      console.log('Power capacity:', data?.result?.power_capacity_mw);
      console.log('Matrix summary:', data?.result?.matrix_summary);
      setMatrix(data);

      // If status is pending, keep polling (don't clear loading state)
      if (data.status === 'pending') {
        return true; // Continue polling
      }

      // If status is failed, set a user-friendly error message
      if (data.status === 'failed') {
        setError('generation_failed');
        setLoading(false);
        return false; // Stop polling
      }

      // Only clear loading state when matrix is complete
      setLoading(false);
      return false; // Stop polling
    } catch (err: any) {
      setError(err.message || 'Failed to load matrix');
      setLoading(false);
      return false; // Stop polling on error
    }
  }, [uuid]);

  const loadProfile = useCallback(async () => {
    try {
      const data = await apiClient.getUserProfile();
      setProfile(data);
    } catch (err: any) {
      console.error('Failed to load profile:', err);
    }
  }, []);

  useEffect(() => {
    if (uuid) {
      loadProfile();

      // Initial load
      loadMatrix().then((shouldContinue) => {
        if (shouldContinue) {
          // Poll every 10 seconds if pending
          const pollInterval = setInterval(async () => {
            const keepPolling = await loadMatrix();
            if (!keepPolling) {
              clearInterval(pollInterval);
            }
          }, 10000);

          return () => clearInterval(pollInterval);
        }
      });
    }
  }, [uuid, loadMatrix, loadProfile]);

  // Rotate loading messages
  useEffect(() => {
    if (matrix?.status === 'pending') {
      const messageInterval = setInterval(() => {
        setLoadingMessageIndex((prev) => (prev + 1) % LOADING_MESSAGES.length);
      }, 3000);

      return () => clearInterval(messageInterval);
    }
  }, [matrix?.status]);

  // Show generating state when matrix is pending
  if (matrix?.status === 'pending') {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-6 px-4">
        <img src="/logo-primary.svg" alt="Generating" className="h-16 w-16 ring-pulse" />
        <div className="text-center">
          <h2 className="text-xl md:text-2xl font-bold mb-2">Generating Your Matrix</h2>
          <p className="text-text-secondary text-sm md:text-base font-mono">
            {LOADING_MESSAGES[loadingMessageIndex]}
          </p>
        </div>
        <div className="text-xs text-text-muted font-mono">
          This usually takes 20-30 seconds
        </div>
      </div>
    );
  }

  // Show simple loading state for initial load (before we know the status)
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <img src="/logo-primary.svg" alt="Loading" className="h-16 w-16 ring-pulse" />
      </div>
    );
  }

  if (error) {
    // Determine user-friendly error message and details
    let errorTitle = 'Something Went Wrong';
    let errorMessage = 'We encountered an issue while processing your request.';
    let errorDetails: string[] = [];
    let showRetry = false;

    if (error === 'generation_failed') {
      errorTitle = 'Matrix Generation Failed';
      errorMessage = 'We were unable to generate your permitting matrix.';
      errorDetails = [
        'This can happen if the AI research process encountered an issue while gathering permitting requirements.',
        'Common causes include temporary API issues or incomplete data for your location.',
      ];
      showRetry = true;
    } else if (error.includes('not found') || error.includes('404')) {
      errorTitle = 'Matrix Not Found';
      errorMessage = 'The requested matrix could not be found.';
      errorDetails = [
        'The matrix may have been deleted or the link may be incorrect.',
      ];
    } else if (error.includes('network') || error.includes('timeout')) {
      errorTitle = 'Connection Issue';
      errorMessage = 'Unable to connect to the server.';
      errorDetails = [
        'Please check your internet connection and try again.',
      ];
      showRetry = true;
    } else {
      // Generic error - still show something user-friendly
      errorDetails = [
        'Please try again in a few moments. If the problem persists, contact support.',
      ];
      showRetry = true;
    }

    return (
      <div className="max-w-3xl mx-auto px-4 md:px-6 py-12 md:py-24">
        <div className="card">
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-500/10 mb-4">
              <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h2 className="text-xl md:text-2xl font-bold mb-2">{errorTitle}</h2>
            <p className="text-text-secondary text-sm md:text-base mb-4">{errorMessage}</p>
          </div>

          {errorDetails.length > 0 && (
            <div className="bg-bg-secondary rounded p-4 mb-6 text-left">
              <ul className="space-y-2 text-sm text-text-secondary">
                {errorDetails.map((detail, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <span className="text-text-muted mt-1">•</span>
                    <span>{detail}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={() => navigate('/dashboard')}
              className="btn-secondary text-sm md:text-base"
            >
              Back to Dashboard
            </button>
            {showRetry && (
              <button
                onClick={() => navigate('/create')}
                className="btn-primary text-sm md:text-base"
              >
                Try Again
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (!matrix) {
    return (
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-12 md:py-24">
        <div className="card text-center">
          <p className="text-text-secondary text-sm md:text-base">Matrix not found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-12">
      {/* Header - Compact, High Density */}
      <div className="flex flex-col md:flex-row md:items-start justify-between gap-6 md:gap-0 mb-8 md:mb-12">
        <div className="flex-1">
          <button
            onClick={() => navigate('/dashboard')}
            className="text-text-muted hover:text-white transition-colors text-xs md:text-sm mb-3 md:mb-4"
          >
            ← Dashboard
          </button>
          <h1 className="text-2xl md:text-3xl font-bold mb-3 md:mb-4">
            {matrix.result.project_name || `${matrix.result.county} Project`}
          </h1>

          {/* Project Metadata - Mono Font */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6 font-mono text-xs md:text-sm">
            <div>
              <div className="text-text-muted text-xs mb-1">LOCATION</div>
              <div className="break-words">
                {matrix.result.city && `${matrix.result.city}, `}
                {matrix.result.county}, {matrix.result.state}
              </div>
            </div>
            <div>
              <div className="text-text-muted text-xs mb-1">TYPE</div>
              <div>{matrix.result.project_type?.replace(/_/g, ' ').toUpperCase()}</div>
            </div>
            <div className="col-span-2 md:col-span-1">
              <div className="text-text-muted text-xs mb-1">CAPACITY</div>
              <div>{matrix.result.power_capacity_mw} MW</div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3 flex-wrap md:flex-nowrap">
          <button onClick={() => window.print()} className="btn-secondary text-xs md:text-sm py-2 flex-1 md:flex-initial whitespace-nowrap">
            Print
          </button>
          {profile &&
           (profile.subscription_status === 'active' ||
            profile.subscription_status === 'canceled' ||
            (profile.subscription_status === 'free' && profile.free_matrices_used < profile.free_matrices_limit)) ? (
            <button onClick={() => navigate('/create')} className="btn-primary text-xs md:text-sm py-2 flex-1 md:flex-initial whitespace-nowrap">
              New Matrix
            </button>
          ) : (
            <button onClick={() => navigate('/pricing')} className="btn-primary text-xs md:text-sm py-2 flex-1 md:flex-initial whitespace-nowrap">
              Upgrade
            </button>
          )}
        </div>
      </div>

      {/* Matrix Summary - Top Level Stats */}
      {matrix.result.matrix_summary && (
        <div className="card mb-6 md:mb-8 bg-bg-primary">
          <h2 className="text-base md:text-lg font-bold mb-4 md:mb-6">Matrix Summary</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            <div>
              <div className="text-text-muted text-xs font-mono mb-2">TOTAL ESTIMATED COST</div>
              <div className="text-2xl font-bold font-mono">
                {matrix.result.matrix_summary.total_cost_formatted || '—'}
              </div>
              <div className="text-text-muted text-xs mt-1">
                Sum of all estimated permit costs
              </div>
            </div>
            <div>
              <div className="text-text-muted text-xs font-mono mb-2">CRITICAL PATH (MAX LEAD TIME)</div>
              <div className="text-2xl font-bold font-mono">
                {matrix.result.matrix_summary.max_lead_time_formatted || '—'}
              </div>
              <div className="text-text-muted text-xs mt-1">
                Longest estimated single permit timeline
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Matrix Data - Structured by Jurisdiction */}
      <div className="card">
        <h2 className="text-base md:text-xl font-bold mb-4 md:mb-6">Permit Requirements</h2>

        {(() => {
          // Use requirements_flat if available (new format), fall back to requirements (legacy)
          const requirements = matrix.result.requirements_flat || matrix.result.requirements || [];

          if (requirements.length > 0) {
            return (
              <div className="overflow-x-auto -mx-4 md:mx-0">
                <div className="inline-block min-w-full align-middle">
                  <div className="overflow-hidden md:rounded">
                    <table className="matrix-table min-w-full">
                <thead>
                  <tr>
                    <th className="w-24">CATEGORY</th>
                    <th className="w-48">REQUIREMENT</th>
                    <th className="w-40">AUTHORITY</th>
                    <th className="w-48">THRESHOLD</th>
                    <th className="w-24">LEAD TIME</th>
                    <th className="w-32">EST. PRICE</th>
                    <th>SOURCES</th>
                  </tr>
                </thead>
                <tbody>
                  {requirements.map((req, idx) => {
                    return (
                      <tr key={idx}>
                        <td className="text-text-secondary font-mono text-xs">{req.category || ''}</td>
                        <td className="text-text-secondary font-mono text-xs">{req.requirement || ''}</td>
                        <td className="text-text-secondary font-mono text-xs">{req.agency || ''}</td>
                        <td className="text-text-secondary font-mono text-xs break-words">
                          {req.threshold || ''}
                        </td>
                        <td className="text-text-secondary font-mono text-xs">
                          {req.timeline && req.timeline !== 'TBD' ? req.timeline : ''}
                        </td>
                        <td className="text-text-secondary font-mono text-xs whitespace-nowrap">
                          {req.estimated_price && req.estimated_price !== 'TBD' ? req.estimated_price : ''}
                        </td>
                        <td className="text-xs">
                          {req.sources && req.sources.length > 0 ? (
                            <div className="flex flex-col gap-1">
                              {req.sources.map((source, sourceIdx) => (
                                <a
                                  key={sourceIdx}
                                  href={source}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-400 hover:text-blue-300 underline break-all"
                                >
                                  {source}
                                </a>
                              ))}
                            </div>
                          ) : (
                            ''
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
                  </div>
                </div>
              </div>
            );
          } else {
            return (
              <div>
                <div className="text-text-muted text-xs mb-4">RAW OUTPUT</div>
                <pre className="bg-black p-4 md:p-6 rounded overflow-auto text-xs font-mono leading-relaxed">
                  {JSON.stringify(matrix.result, null, 2)}
                </pre>
              </div>
            );
          }
        })()}
      </div>
    </div>
  );
};
